import React, { useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Button } from './Button';
import { Input } from './Input';
import { Colors, Typography } from '../constants';
import { authService } from '../services/auth';
import { useDialog } from '../contexts';

interface ChangePasswordFormProps {
  /** Hide the in-form heading when the screen header already shows it (mobile) */
  showTitle?: boolean;
  onDone: () => void;
  onCancel: () => void;
}

export const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({ onDone, onCancel, showTitle = true }) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentPasswordError, setCurrentPasswordError] = useState('');
  const [newPasswordError, setNewPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCurrentPasswordChange = (text: string) => {
    setCurrentPassword(text);
    if (currentPasswordError) setCurrentPasswordError('');
  };

  const handleNewPasswordChange = (text: string) => {
    setNewPassword(text);
    if (newPasswordError) setNewPasswordError('');
    if (confirmPasswordError && confirmPassword) {
      setConfirmPasswordError('');
    }
  };

  const handleConfirmPasswordChange = (text: string) => {
    setConfirmPassword(text);
    if (confirmPasswordError) setConfirmPasswordError('');
  };

  const handleCurrentPasswordBlur = () => {
    if (!currentPassword) {
      setCurrentPasswordError(t('common.validation.passwordRequired'));
    }
  };

  const handleNewPasswordBlur = () => {
    if (!newPassword) {
      setNewPasswordError(t('common.validation.passwordRequired'));
    } else if (newPassword.length < 6) {
      setNewPasswordError(t('common.validation.passwordMinLength'));
    }
  };

  const handleConfirmPasswordBlur = () => {
    if (!confirmPassword) {
      setConfirmPasswordError(t('common.validation.passwordRequired'));
    } else if (confirmPassword !== newPassword) {
      setConfirmPasswordError(t('common.validation.passwordMismatch'));
    }
  };

  const handleSubmit = async () => {
    setCurrentPasswordError('');
    setNewPasswordError('');
    setConfirmPasswordError('');

    let valid = true;
    if (!currentPassword) {
      setCurrentPasswordError(t('common.validation.passwordRequired'));
      valid = false;
    }
    if (!newPassword) {
      setNewPasswordError(t('common.validation.passwordRequired'));
      valid = false;
    } else if (newPassword.length < 6) {
      setNewPasswordError(t('common.validation.passwordMinLength'));
      valid = false;
    }
    if (!confirmPassword) {
      setConfirmPasswordError(t('common.validation.passwordRequired'));
      valid = false;
    } else if (confirmPassword !== newPassword) {
      setConfirmPasswordError(t('common.validation.passwordMismatch'));
      valid = false;
    }
    if (!valid) return;

    setLoading(true);
    try {
      // Step 1: Verify current password
      const user = await authService.getCurrentUser();
      if (!user) throw new Error('Not authenticated');
      await authService.verifyCurrentPassword(user.email, currentPassword);

      // Step 2: Update to new password
      await authService.updatePassword(newPassword);

      dialog.alert(
        t('screens.changePassword.successTitle'),
        t('screens.changePassword.successMessage'),
        'success'
      );
      onDone();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : '';
      if (errorMessage.includes('Invalid login credentials')) {
        setCurrentPasswordError(t('screens.changePassword.incorrectCurrentPassword'));
      } else {
        dialog.alert(
          t('screens.changePassword.failed'),
          errorMessage || t('common.errors.unknown'),
          'error'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      {showTitle ? <Text style={styles.title}>{t('screens.changePassword.title')}</Text> : null}
      <View style={styles.form}>
        <Input
          label={t('screens.changePassword.currentPassword')}
          value={currentPassword}
          onChangeText={handleCurrentPasswordChange}
          onBlur={handleCurrentPasswordBlur}
          placeholder={t('screens.changePassword.currentPasswordPlaceholder')}
          secureTextEntry
          autoComplete="current-password"
          returnKeyType="next"
          error={currentPasswordError}
        />
        <Input
          label={t('screens.changePassword.newPassword')}
          value={newPassword}
          onChangeText={handleNewPasswordChange}
          onBlur={handleNewPasswordBlur}
          placeholder={t('screens.changePassword.newPasswordPlaceholder')}
          secureTextEntry
          autoComplete="new-password"
          returnKeyType="next"
          error={newPasswordError}
        />
        <Input
          label={t('screens.changePassword.confirmPassword')}
          value={confirmPassword}
          onChangeText={handleConfirmPasswordChange}
          onBlur={handleConfirmPasswordBlur}
          placeholder={t('screens.changePassword.confirmPasswordPlaceholder')}
          secureTextEntry
          autoComplete="new-password"
          returnKeyType="go"
          onSubmitEditing={handleSubmit}
          error={confirmPasswordError}
        />
      </View>
      <View style={styles.buttons}>
        <Button
          title={t('common.buttons.cancel')}
          onPress={onCancel}
          variant="outline"
          style={styles.buttonHalf}
        />
        <Button
          title={t('screens.changePassword.submit')}
          onPress={handleSubmit}
          loading={loading}
          style={styles.buttonHalf}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  title: {
    ...Typography.section,
    color: Colors.textDark,
    marginBottom: 24,
  },
  form: {
    gap: 16,
    marginBottom: 24,
  },
  buttons: {
    flexDirection: 'row',
    gap: 12,
  },
  buttonHalf: {
    flex: 1,
  },
});
